/**
 * Object paths inside Storage buckets.
 * A stored property or task photo may be a public object address, a signed
 * address, or a bucket path. The address is a locator. Reads use a signed URL.
 * CommonJS so Netlify functions can require this file.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function decodePath(value) {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return '';
  }
}

function safePath(path) {
  if (!path || path.charAt(0) === '/' || path.charAt(path.length - 1) === '/') return '';
  if (path.indexOf('//') !== -1 || path.indexOf('..') !== -1 || path.indexOf('\\') !== -1) return '';
  return path;
}

function objectPathFromStored(stored, bucket) {
  const value = typeof stored === 'string' ? stored.trim() : '';
  const name = String(bucket || '').trim();
  if (!value || !name || value.indexOf('..') !== -1 || value.indexOf('\\') !== -1 || value.indexOf('\0') !== -1) {
    return '';
  }
  const markers = [
    '/object/public/' + name + '/',
    '/object/sign/' + name + '/',
    '/object/authenticated/' + name + '/',
  ];
  let path = '';
  for (let i = 0; i < markers.length; i += 1) {
    const at = value.indexOf(markers[i]);
    if (at >= 0) {
      path = value.slice(at + markers[i].length);
      break;
    }
  }
  if (!path && value.indexOf('://') === -1) {
    let relative = value.replace(/^\/+/, '');
    const prefix = name + '/';
    const publicPrefix = 'storage/v1/object/public/' + prefix;
    if (relative.indexOf(publicPrefix) === 0) relative = relative.slice(publicPrefix.length);
    else if (relative.indexOf(prefix) === 0) relative = relative.slice(prefix.length);
    path = relative;
  }
  if (!path) return '';
  path = path.split('?')[0].split('#')[0];
  path = decodePath(path).replace(/^\/+/, '');
  return safePath(path);
}

function classifyStoredPhoto(stored, bucket) {
  const value = typeof stored === 'string' ? stored.trim() : '';
  const name = String(bucket || '').trim();
  if (!value || !name) return { kind: 'empty', src: '', path: '' };
  if (value.indexOf('/object/sign/' + name + '/') !== -1) {
    return { kind: 'signed', src: value, path: objectPathFromStored(value, name) };
  }
  const path = objectPathFromStored(value, name);
  if (path) return { kind: 'object', src: '', path: path };
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return { kind: 'external', src: value, path: '' };
  return { kind: 'empty', src: '', path: '' };
}

function propertyPhotoPathForClient(stored, clientId) {
  const id = String(clientId || '');
  if (!UUID.test(id)) return '';
  const path = objectPathFromStored(stored, 'property-photos');
  if (!path) return '';
  if (path.indexOf(id + '/') === 0) return path;
  if (path.indexOf('/') === -1 && path.indexOf('_' + id + '_') > 0) return path;
  return '';
}

function libraryPhotoObjectPath(stored) {
  const path = objectPathFromStored(stored, 'task-photos');
  if (!path) return '';
  const slash = path.indexOf('/');
  if (slash > 0 && UUID.test(path.slice(0, slash))) return path;
  if (slash === -1 && /^task_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i.test(path)) {
    return path;
  }
  return '';
}

function profileImageObjectPath(profileId, fileName) {
  const id = String(profileId || '');
  if (!UUID.test(id)) return '';
  const name = String(fileName || '').replace(/[^a-zA-Z0-9._-]/g, '_');
  if (!name || name === '_' || name.indexOf('..') !== -1) return '';
  return id + '/' + name;
}

module.exports = {
  objectPathFromStored,
  classifyStoredPhoto,
  propertyPhotoPathForClient,
  libraryPhotoObjectPath,
  profileImageObjectPath,
};
